import * as THREE from 'three';
import { LightmapData } from "../loaders/lit";
import { TextureManager } from "./texturemanager";

/**
 * The lightmap data for a particular object and part in a MapZoneChunk.
 *
 * @constructor
 */
export class LightmapManagerData {
    objectsPerRow: number;
    objectIndex: number;
    texture: any;

    constructor() {
        this.objectsPerRow = 0;
        this.objectIndex = 0;
        this.texture = null;
    }
}

/**
 * A manager for handling the textures for various parts as defined
 * by the managed lightmap data.
 *
 * @constructor
 * @param {LightmapData} data
 */
export class LightmapManager {
    data: any;
    textures: any;
    static Data = LightmapManagerData;

    constructor(data: any) {
        this.data = data;
        this.textures = {};
    }

    /**
     * Retrieves the lightmap data for a particular part.
     *
     * @param {number} objectIdx
     * The object index from the MapZoneChunk.
     * @param {number} partIdx
     * The part index from the referenced object.
     * @returns {LightmapManager.Data}
     */
    getDataForPart(objectIdx: any, partIdx: any) {
        const lmModel = this.data.objects[objectIdx];
        if (!lmModel) {
            return null;
        }

        const lmPart = lmModel.parts[partIdx];
        if (!lmPart) {
            return null;
        }

        const lmData = new LightmapManager.Data();
        lmData.objectsPerRow = lmPart.objectsPerRow;
        lmData.objectIndex = lmPart.objectIndex;
        lmData.texture = this._getTexture(lmPart.lightmapIndex);
        return lmData;
    }

    /**
     * Loads and caches the texture referenced by the lightmap data.
     *
     * @param {number} textureIdx
     * @returns {THREE.Texture}
     * @private
     */
    _getTexture(textureIdx: any) {
        const foundTexture = this.textures[textureIdx];
        if (foundTexture) {
            return foundTexture;
        }

        const embedded = this.data.embeddedTextures?.[textureIdx];
        let newTexture: any;
        if (embedded?.rgbaBase64 && embedded.width > 0 && embedded.height > 0) {
            const decoded = atob(embedded.rgbaBase64);
            const pixels = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
            newTexture = new THREE.DataTexture(
                pixels,
                embedded.width,
                embedded.height,
                THREE.RGBAFormat,
            );
            newTexture.colorSpace = THREE.LinearSRGBColorSpace;
            newTexture.needsUpdate = true;
        } else {
            newTexture = TextureManager.load(this.data.textures[textureIdx]);
        }
        newTexture.wrapS = THREE.ClampToEdgeWrapping;
        newTexture.wrapT = THREE.ClampToEdgeWrapping;
        this.textures[textureIdx] = newTexture;
        return newTexture;
    }

    /**
     * This is a helper to allow a LightmapManager to be used by the DataManager.
     *
     * @param {string} path
     * The path to the lightmap data.
     * @param {Function} [callback]
     */
    static load(path: any, callback: any) {
        LightmapData.load(path, function (data: any) {
            callback(new LightmapManager(data));
        });
    }
}
